import type * as Y from 'yjs';
import { Frame, type IFrame } from '../../../core/frames/IFrame';
import type { FrameManager } from '../../../core/frames/FrameManager';

/** React snapshots must not share mutable frame identity with tool state. */
export function snapshotFrames(manager: FrameManager): IFrame[] {
  return manager.getAllFrames().map(frame => Frame.fromJSON(frame.toJSON()));
}

/** Project stored frames into both the manager and the rendered frame list.
 * Initial hydration must not depend on frame event listeners already existing.
 */
export function observeYjsFrames(
  map: Y.Map<ReturnType<Frame['toJSON']>>,
  manager: FrameManager,
  guards: { toYjs: { current: boolean }; fromYjs: { current: boolean } },
  publish: (frames: IFrame[]) => void,
): () => void {
  const applyRecord = (id: string) => {
    const record = map.get(id);
    if (!record) return;
    const restored = Frame.fromJSON({ ...record, id });
    const existing = manager.getFrame(id);
    if (existing) {
      // Preserve references held by tools while replacing membership, hierarchy,
      // visibility and styling as well as geometry.
      Object.assign(existing, restored);
      existing.setParentFrame(restored.parentFrame);
    } else {
      manager.addFrame(restored);
    }
  };
  const apply = (update: () => void) => {
    guards.fromYjs.current = true;
    try {
      update();
      publish(snapshotFrames(manager));
    } finally {
      guards.fromYjs.current = false;
    }
  };
  const onChange = (event: Y.YMapEvent<ReturnType<Frame['toJSON']>>) => {
    if (guards.toYjs.current) return;
    apply(() => {
      event.changes.keys.forEach((change, id) => {
        if (change.action === 'delete') manager.removeFrame(id);
      });
      // Removal detaches children; apply surviving records afterwards so a
      // reparent-and-delete transaction keeps its stored hierarchy.
      event.changes.keys.forEach((change, id) => {
        if (change.action !== 'delete') applyRecord(id);
      });
    });
  };
  map.observe(onChange);
  try {
    apply(() => map.forEach((_record, id) => applyRecord(id)));
  } catch (error) {
    map.unobserve(onChange);
    throw error;
  }
  return () => map.unobserve(onChange);
}
